import { useState } from "react";
import { Building2, User, Loader2, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const EMPTY = {
  client_type: "entreprise", raison_sociale: "", trade_name: "",
  contact_first_name: "", contact_last_name: "", email: "", phone: "",
  address: "", address2: "", zip_code: "", city: "", country: "France",
  siret: "", vat_number: "",
};

function Field({ label, k, form, set, required = false, type = "text", testId, placeholder = "" }) {
  return (
    <div>
      <Label>{label}{required && " *"}</Label>
      <Input type={type} className="mt-1.5" value={form[k]} placeholder={placeholder}
        onChange={(e) => set(k, e.target.value)} data-testid={testId} required={required} />
    </div>
  );
}

export default function QuickClientDialog({ open, onClose, onDone }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [duplicates, setDuplicates] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const isEntreprise = form.client_type === "entreprise";

  const reset = () => { setForm(EMPTY); setDuplicates(null); };
  const close = () => { reset(); onClose(); };

  const submit = async (force = false) => {
    if (saving) return;
    setSaving(true);
    try {
      const { data } = await api.post("/clients/quick-create", { ...form, force });
      if (data.duplicates) {
        setDuplicates(data.duplicates);
      } else {
        toast.success(`Client « ${data.client.name} » enregistré dans Clients non récurrents`);
        reset();
        onDone(data.client);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la création du client");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-xl" data-testid="quick-client-dialog">
        <DialogHeader><DialogTitle>Nouveau client (facturation)</DialogTitle></DialogHeader>

        {duplicates ? (
          <div className="space-y-4" data-testid="quick-client-duplicates">
            <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg p-4">
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-sm text-amber-900">Un client similaire existe déjà. Souhaitez-vous utiliser cette fiche ?</p>
                <div className="mt-2 space-y-1.5">
                  {duplicates.map((d) => (
                    <button key={d.id} onClick={() => { reset(); onDone(d); }}
                      data-testid={`quick-client-use-${d.id}`}
                      className="w-full text-left bg-white border border-neutral-200 hover:border-neutral-400 rounded-lg px-3 py-2 text-sm transition-colors">
                      <span className="font-medium">{d.name}</span>
                      <span className="text-neutral-500"> {d.email ? `· ${d.email}` : ""} {d.billing_siret ? `· SIRET ${d.billing_siret}` : ""} · {d.kind === "ponctuel" ? "non récurrent" : "récurrent"}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setDuplicates(null)} data-testid="quick-client-dups-back">Retour au formulaire</Button>
              <Button onClick={() => submit(true)} disabled={saving} className="bg-neutral-900 text-white" data-testid="quick-client-force">
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-1.5" />}Créer quand même un nouveau client
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex gap-2" data-testid="quick-client-type">
              {[{ v: "entreprise", l: "Entreprise", I: Building2 }, { v: "particulier", l: "Particulier", I: User }].map(({ v, l, I }) => (
                <button key={v} type="button" onClick={() => set("client_type", v)}
                  data-testid={`quick-client-type-${v}`}
                  className={`flex-1 flex items-center justify-center gap-2 rounded-lg border px-3 py-2.5 text-sm font-medium transition-colors ${isEntreprise === (v === "entreprise") ? "border-neutral-900 bg-neutral-900 text-white" : "border-neutral-200 bg-white text-neutral-600 hover:border-neutral-400"}`}>
                  <I className="w-4 h-4" /> {l}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {isEntreprise ? (
                <>
                  <Field label="Raison sociale" k="raison_sociale" form={form} set={set} required testId="qc-raison" />
                  <Field label="Nom commercial (si différent)" k="trade_name" form={form} set={set} testId="qc-trade" />
                </>
              ) : null}
              <Field label={isEntreprise ? "Prénom du contact" : "Prénom"} k="contact_first_name" form={form} set={set} required={!isEntreprise} testId="qc-firstname" />
              <Field label={isEntreprise ? "Nom du contact" : "Nom"} k="contact_last_name" form={form} set={set} required={!isEntreprise} testId="qc-lastname" />
              <Field label="Adresse e-mail" k="email" form={form} set={set} required type="email" testId="qc-email" />
              <Field label="Téléphone" k="phone" form={form} set={set} testId="qc-phone" />
              <div className="sm:col-span-2"><Field label="Adresse" k="address" form={form} set={set} testId="qc-address" /></div>
              <div className="sm:col-span-2"><Field label="Complément d'adresse" k="address2" form={form} set={set} testId="qc-address2" /></div>
              <Field label="Code postal" k="zip_code" form={form} set={set} testId="qc-zip" />
              <Field label="Ville" k="city" form={form} set={set} testId="qc-city" />
              <Field label="Pays" k="country" form={form} set={set} testId="qc-country" />
              {isEntreprise ? (
                <>
                  <Field label="SIREN / SIRET (si applicable)" k="siret" form={form} set={set} testId="qc-siret" />
                  <Field label="N° TVA intracommunautaire (si applicable)" k="vat_number" form={form} set={set} testId="qc-vat" placeholder="FR…" />
                </>
              ) : null}
            </div>
            <p className="text-xs text-neutral-400">Seuls les champs marqués * sont obligatoires pour établir la facture.</p>

            <div className="flex gap-2 justify-end pt-1">
              <Button variant="outline" onClick={close} data-testid="quick-client-cancel">Annuler</Button>
              <Button onClick={() => submit(false)} disabled={saving} className="bg-neutral-900 text-white" data-testid="quick-client-save">
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-1.5" />}Enregistrer et sélectionner
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
